/**
 * 一条学习记录（学习列表里点开）：时间跟着它的学习日程（改时间、重复去时间表里编辑日程），
 * 这里只改这一次的奖励（完成奖励、按时奖励）、跳过 / 恢复、撤回。PATCH / DELETE assignments/{id}，见 docs/API.md 2.7。
 */
import { useState } from "react";
import { confirm, deleteEntity, eventTimeMode, isDone, ON_TIME_BONUS, run, useCalendar, useStore, writeEntity, type Assignment, type ScheduleEvent } from "../core";
import { Button, FormCard, ModalHeader, NumberStepper, Sheet, SheetBody, ValueRow } from "../ui";
import { dayLabel } from "../pages/learn/activity";

export function TaskEditSheet({ assignmentId, onClose }: { assignmentId: string | null; onClose: () => void }) {
  const a = useStore((s) => (assignmentId ? s.assignments[assignmentId] : undefined));
  return (
    <Sheet open={!!assignmentId} onClose={onClose} width={520} persistent label="学习任务">
      {a && <TaskForm key={a.id} a={a} onClose={onClose} />}
    </Sheet>
  );
}

/** 这次的时间："20:00 前" / "16:00–16:30" / "全天" */
function eventTimeText(ev: ScheduleEvent | undefined, a: Assignment, cal: ReturnType<typeof useCalendar>): string {
  if (!ev) return a.dueAt != null ? `${cal.timeString(a.dueAt)} 前` : "";
  const mode = eventTimeMode(ev);
  if (mode === "all_day") return "全天";
  if (mode === "due") return `${ev.startTime} 前`;
  if (mode === "point") return ev.startTime;
  const start = cal.ms(a.plannedDate, ev.startTime);
  return start == null ? ev.startTime : `${ev.startTime}–${cal.timeString(start + ev.durationMin * 60_000)}`;
}

function TaskForm({ a, onClose }: { a: Assignment; onClose: () => void }) {
  const cal = useCalendar();
  const content = useStore((s) => s.contents[a.contentId]);
  const child = useStore((s) => s.children[a.childId]);
  const event = useStore((s) => (a.scheduleEventId ? s.scheduleEvents[a.scheduleEventId] : undefined));
  const [reward, setReward] = useState(a.pointsReward);
  const [bonus, setBonus] = useState(a.onTimeBonus ?? ON_TIME_BONUS);
  const [busy, setBusy] = useState(false);
  const done = isDone(a);
  const skipped = a.status === "skipped";

  const patch = async (body: object, msg: string) => {
    setBusy(true);
    const ok = await run(() => writeEntity("assignments", "PATCH", `assignments/${a.id}`, body), msg);
    setBusy(false);
    if (ok) onClose();
  };

  const withdraw = async () => {
    const repeating = !!event?.rrule;
    const ok = await confirm({
      title: `撤回「${content?.title ?? "这个学习任务"}」？`,
      message: repeating ? "只撤回这一天，以后的照常；要全部撤回请在时间表里删除这个日程。" : "孩子那边不会再显示这个任务，时间表上的日程一起删除。",
      confirmText: "撤回",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    const res = await run(() => deleteEntity("assignments", a.id, `assignments/${a.id}`).then(() => true), "已撤回");
    setBusy(false);
    if (res) onClose();
  };

  return (
    <>
      <ModalHeader
        title="学习任务"
        actionTitle="保存"
        actionEnabled={!done && (reward !== a.pointsReward || bonus !== (a.onTimeBonus ?? ON_TIME_BONUS))}
        busy={busy}
        onCancel={onClose}
        onAction={() => void patch({ pointsReward: reward, onTimeBonus: bonus }, "已保存")}
      />
      <SheetBody gap={12}>
        <div className="learn-detail-head">
          <div>
            <h2>{content?.title ?? "学习任务"}</h2>
            <p>
              {child?.name ?? ""} · {dayLabel(cal, a.plannedDate)} {cal.monthDay(a.plannedDate)} {eventTimeText(event, a, cal)}
              {done ? " · 已完成" : skipped ? " · 已跳过" : ""}
            </p>
          </div>
        </div>
        <p className="learn-note">时间、重复跟着时间表里的日程「{event?.title ?? content?.title ?? "学习"}」，要改请到时间表里编辑这个日程。</p>
        {!done && (
          <FormCard>
            <ValueRow title="这次的奖励" height={56}>
              <NumberStepper value={reward} min={0} max={100} onChange={setReward} />
            </ValueRow>
            {a.dueAt != null && (
              <ValueRow title="按时完成另加" height={56}>
                <NumberStepper value={bonus} min={0} max={100} onChange={setBonus} />
              </ValueRow>
            )}
          </FormCard>
        )}
        {!done && (
          <Button variant="soft" block onClick={() => void patch({ status: skipped ? "planned" : "skipped" }, skipped ? "已恢复" : "这次跳过")} disabled={busy}>
            {skipped ? "恢复这一次" : "这次跳过"}
          </Button>
        )}
        <Button variant="soft" block onClick={() => void withdraw()} disabled={busy}>
          撤回这个任务
        </Button>
      </SheetBody>
    </>
  );
}
